import React, { useEffect, useState, useMemo } from 'react';
import { Typography, Row, Col, Layout, Button, Card, Progress } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { getExamById } from '../../../stateManager/reducers/studentAuthSlice';
import Spinner from '../../Common/Spinner';
import MCQComponent from '../../Questions/students/MCQ.component';
import CheckBoxComponent from '../../Questions/students/CheckBox.component';
import ParagraphComponent from '../../Questions/students/Paragraph.component';
import ShortAnsComponent from '../../Questions/students/ShortAns.component';
import MCAComponent from '../../Questions/students/MCA.component';
import { compareArray } from '../../../constants/constFunction';
import { instance } from '../../../constants/constString';

const { Content } = Layout;
const { Text, Title } = Typography;

const getQuestionKey = (question, index) =>
	question?._id ||
	question?.questionId?._id ||
	question?.question?._id ||
	question?.id ||
	`question-${index}`;

const QuickPracticeExam = props => {
	const { groupId, examId } = props.match.params;
	const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
	const [isLoading, setIsLoading] = useState(false);
	const [userAnswers, setUserAnswers] = useState({});
	const [mode, setMode] = useState('exam'); // 'exam', 'reading', 'bookmarks'
	const [bookmarkedQuestionIds, setBookmarkedQuestionIds] = useState(new Set());
	const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);

	const dispatch = useDispatch();
	const history = useHistory();

	const examData = useSelector(state => state.studentAuth.currentExam);
	// eslint-disable-next-line no-unused-vars
	const allSubjects = JSON.parse(localStorage.getItem('allSubjects') || '[]');

	const student = useMemo(() => {
		try {
			return JSON.parse(localStorage.getItem('student') || '{}');
		} catch (error) {
			return {};
		}
	}, []);

	const studentId = student?.id || student?._id;

	useEffect(() => {
		// Remove localSavedAnswers from localStorage on page load/reload
		// Quick practice should always start fresh
		localStorage.removeItem('localSavedAnswers');
		
		window.addEventListener('contextmenu', event => event.preventDefault());
		return () => {
			window.removeEventListener('contextmenu', event => event.preventDefault());
		};
	}, []);

	useEffect(() => {
		async function fetchData() {
			setIsLoading(true);
			// Fetch exam with answers since isInstantPracticeExam is true
			await dispatch(getExamById({ examId: examId, groupId: groupId }));
			// console.log('QuickPracticeExam - Exam Data:', result?.payload?.data);
			// if (result?.payload?.data?.questions) {
			// 	console.log('First question structure:', result.payload.data.questions[0]);
			// 	console.log('First question has answer:', !!result.payload.data.questions[0]?.answer);
			// 	console.log('First question has explanation:', !!result.payload.data.questions[0]?.explanation);
			// }
			setIsLoading(false);
		}
		fetchData();
	}, [examId, dispatch, groupId]);

	// Fetch bookmarked questions for this exam
	useEffect(() => {
		async function fetchBookmarks() {
			if (!studentId || !examData?.questions) return;
			setIsLoadingBookmarks(true);
			try {
				const response = await instance.get('/student-question-bookmark', {
					params: {
						student_id: studentId,
					}
				});
				const bookmarks = response?.data?.data || [];
				// Filter bookmarks for questions in this exam
				const examQuestionIds = new Set(
					examData.questions.map(q => q._id || q.questionId?._id || q.question?._id)
				);
				const relevantBookmarkIds = bookmarks
					.filter(bookmark => {
						const qId = bookmark.question_id?._id || bookmark.question_id;
						return examQuestionIds.has(qId);
					})
					.map(bookmark => bookmark.question_id?._id || bookmark.question_id);
				setBookmarkedQuestionIds(new Set(relevantBookmarkIds));
			} catch (error) {
				console.error('Error fetching bookmarks:', error);
			} finally {
				setIsLoadingBookmarks(false);
			}
		}
		if (examData?.questions) {
			fetchBookmarks();
		}
	}, [studentId, examData?.questions]);

	const handleAnswerSelect = (questionKey, answer) => {
		// console.log('QuickPracticeExam -> handleAnswerSelect', { questionKey, answer });
		setUserAnswers(prev => ({
			...prev,
			[questionKey]: answer
		}));
		
		if (!answeredQuestions.has(questionKey)) {
			setAnsweredQuestions(prev => new Set([...prev, questionKey]));
		}
	};

	// Filter questions based on mode (calculate early for use in functions)
	const filteredQuestions = useMemo(() => {
		if (!examData?.questions) return [];
		if (mode === 'bookmarks') {
			return examData.questions.filter(question => {
				const questionId = question._id || question.questionId?._id || question.question?._id;
				return bookmarkedQuestionIds.has(questionId);
			});
		}
		return examData.questions;
	}, [examData?.questions, mode, bookmarkedQuestionIds]);

	const getProgress = () => {
		if (!filteredQuestions || filteredQuestions.length === 0) return 0;
		// Count answered questions that are in the filtered list
		let answeredInFiltered = 0;
		filteredQuestions.forEach((question, index) => {
			const questionKey = getQuestionKey(question, index);
			if (answeredQuestions.has(questionKey)) {
				answeredInFiltered++;
			}
		});
		return Math.round((answeredInFiltered / filteredQuestions.length) * 100);
	};

	const getCorrectlyAnsweredCount = () => {
		if (!filteredQuestions || filteredQuestions.length === 0) return 0;
		
		let correctCount = 0;
		filteredQuestions.forEach((question, index) => {
			const questionKey = getQuestionKey(question, index);
			const userAnswer = userAnswers[questionKey];
			
			if (!userAnswer || !answeredQuestions.has(questionKey)) {
				return; // Skip unanswered questions
			}

			const correctAnswer = question.answer || [];
			const questionType = question.type;

			// For MCQ and checkbox: compare arrays
			if (questionType === 'MCQ' || questionType === 'checkbox') {
				const userAnsArray = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
				const correctAnsArray = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];
				if (compareArray(userAnsArray, correctAnsArray)) {
					correctCount++;
				}
			}
			// For MCA: compare each option individually
			else if (questionType === 'MCA') {
				const userAnsArray = Array.isArray(userAnswer) ? userAnswer : [];
				const correctAnsArray = Array.isArray(correctAnswer) ? correctAnswer : [];
				if (userAnsArray.length === correctAnsArray.length) {
					let allCorrect = true;
					for (let i = 0; i < correctAnsArray.length; i++) {
						if (userAnsArray[i] !== correctAnsArray[i]) {
							allCorrect = false;
							break;
						}
					}
					if (allCorrect) {
						correctCount++;
					}
				}
			}
			// For shortAns and paragraph: text comparison (case-insensitive, trimmed)
			else if (questionType === 'shortAns' || questionType === 'paragraph') {
				const userAnsText = (Array.isArray(userAnswer) ? userAnswer[0] : userAnswer || '').toString().trim().toLowerCase();
				const correctAnsText = (Array.isArray(correctAnswer) ? correctAnswer[0] : correctAnswer || '').toString().trim().toLowerCase();
				if (userAnsText === correctAnsText) {
					correctCount++;
				}
			}
		});
		
		return correctCount;
	};

	if (isLoading || !examData || !examData.questions) {
		return <Spinner />;
	}

	const totalQuestions = filteredQuestions.length;
	const progress = getProgress();
	const correctlyAnswered = getCorrectlyAnsweredCount();

	// Create question components with instant feedback
	const renderQuestion = (question, index) => {
		const questionKey = getQuestionKey(question, index);
		const userAnswer = userAnswers[questionKey];
		const isAnswered = answeredQuestions.has(questionKey);
		
		// INSTANT ANSWER FEATURE (Currently Disabled)
		// ==========================================
		// To enable showing answer immediately after option selection:
		// Change: const shouldShowAnswer = mode === 'reading' || mode === 'bookmarks';
		// To:     const shouldShowAnswer = mode === 'reading' || mode === 'bookmarks' || isAnswered;
		// 
		// When isAnswered is included:
		// - Answer and explanation will show immediately after user selects an option
		// - isResult prop becomes true, triggering result view in child components
		// - User can see correct/wrong feedback instantly
		// 
		// Currently disabled: Only shows answers in reading/bookmarks mode
		// ==========================================
		const shouldShowAnswer = mode === 'reading' || mode === 'bookmarks';
		// In reading/bookmarks mode, always show correct answer. In exam mode, show user's answer if answered
		const showCorrectAnswer = mode === 'reading' || mode === 'bookmarks';
		
		// For instant practice, we always have answer and explanation available
		// Format question object to work with existing components that expect result format
		// When answered or in reading/bookmarks mode, show result format with correct answer
		const questionForDisplay = {
			...question,
			// Always include questionId structure for components to access answer/explanation
			questionId: {
				...question,
				_id: question._id,
				title: question.title,
				options: question.options || [],
				answer: question.answer || [],
				explanation: question.explanation || '',
				explanationExt: question.explanationExt || [],
				image: question.image || [],
				file: question.file || [],
				optionType: question.optionType,
				type: question.type,
				status: question.status
			},
			// Show correct answer in reading/bookmarks mode, or user's answer in exam mode when answered
			answer: shouldShowAnswer
				? (showCorrectAnswer
					? (Array.isArray(question.answer) ? question.answer : (question.answer ? [question.answer] : []))
					: (isAnswered 
						? (Array.isArray(userAnswer) ? userAnswer : (userAnswer ? [userAnswer] : []))
						: []))
				: [],
			marks: question.point
		};

		// Debug: Log question structure for first question
		// if (index === 0 && isAnswered) {
		// 	console.log('Question 1 - isAnswered:', isAnswered);
		// 	console.log('Question 1 - userAnswer:', userAnswer);
		// 	console.log('Question 1 - correctAnswer:', question.answer);
		// 	console.log('Question 1 - explanation:', question.explanation);
		// 	console.log('Question 1 - questionForDisplay:', questionForDisplay);
		// }

		const commonProps = {
			question: questionForDisplay,
			questionIndex: index,
			examId: examId,
			groupId: groupId,
			isResult: shouldShowAnswer, // Show result format when answered or in reading/bookmarks mode
			examMeta: {
				multipleTimesSubmission: mode === 'exam', // Allow changing answers only in exam mode
				isInstantPractice: true,
				examId: examData
			},
			onAnswerSelect: mode === 'exam' ? (answer => handleAnswerSelect(questionKey, answer)) : undefined,
			isPracticeQuiz: true
		};

		switch (question.type) {
			case 'MCQ':
				return <MCQComponent {...commonProps} />;
			case 'MCA':
				return <MCAComponent {...commonProps} />;
			case 'checkbox':
				return <CheckBoxComponent {...commonProps} />;
			case 'shortAns':
				return <ShortAnsComponent {...commonProps} />;
			case 'paragraph':
				return <ParagraphComponent {...commonProps} />;
			default:
				return null;
		}
	};

	return (
		<Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
			<Content style={{ padding: '20px' }}>
				{/* Header - Fixed */}
				<Card style={{ marginBottom: 20, position: 'sticky', top: 0, zIndex: 100, background: '#fff' }}>
					<Row justify="space-between" align="middle">
						<Col>
							<Button
								icon={<ArrowLeftOutlined />}
								onClick={() => history.goBack()}
							>
								Back
							</Button>
						</Col>
						<Col className='text-center'>
							<Title level={4} style={{ margin: 0 }}>
								{examData.title || 'Quick Practice Exam'}
							</Title>
							<Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
								{correctlyAnswered} / {totalQuestions} Correct
							</Text>
							<div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
								<Button
									type={mode === 'exam' ? 'primary' : 'default'}
									size="small"
									onClick={() => setMode('exam')}
								>
									Exam
								</Button>
								<Button
									type={mode === 'reading' ? 'primary' : 'default'}
									size="small"
									onClick={() => setMode('reading')}
								>
									Reading
								</Button>
								<Button
									type={mode === 'bookmarks' ? 'primary' : 'default'}
									size="small"
									onClick={() => setMode('bookmarks')}
									loading={isLoadingBookmarks}
								>
									Bookmarks {mode === 'bookmarks' && `(${filteredQuestions.length})`}
								</Button>
							</div>
						</Col>
						<Col>
							<Text strong>
								{(() => {
									let answeredInFiltered = 0;
									filteredQuestions.forEach((question, index) => {
										const questionKey = getQuestionKey(question, index);
										if (answeredQuestions.has(questionKey)) {
											answeredInFiltered++;
										}
									});
									return answeredInFiltered;
								})()} / {totalQuestions} Answered
							</Text>
						</Col>
					</Row>
					<Row style={{ marginTop: 15 }}>
						<Col span={24}>
							<Progress 
								percent={progress} 
								status="active"
								showInfo={true}
							/>
						</Col>
					</Row>
				</Card>

				{/* All Questions - Scrollable List */}
				<div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
					{filteredQuestions.length === 0 && mode === 'bookmarks' ? (
						<Card>
							<Text type="secondary" style={{ fontSize: 16 }}>
								No bookmarked questions found in this exam.
							</Text>
						</Card>
					) : (
						filteredQuestions.map((question, index) => {
							const questionKey = getQuestionKey(question, index);
							return (
								<Card 
									key={questionKey} 
									style={{ marginBottom: 20 }}
								>
									<Row>
										<Col span={24}>
											<Text strong style={{ fontSize: 16, color: '#1890ff' }}>
												Question {index + 1} of {totalQuestions}
											</Text>
										</Col>
									</Row>
									<Row style={{ marginTop: 20 }}>
										<Col span={24}>
											{renderQuestion(question, index)}
										</Col>
									</Row>
								</Card>
							);
						})
					)}
				</div>
			</Content>
		</Layout>
	);
};

export default QuickPracticeExam;

